import { motion } from "framer-motion";
import { Monitor, Tablet, Smartphone } from "lucide-react";
import { IMAGES } from "@/site/content";

export const Portfolio = () => {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-20 md:py-28 bg-gray-50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-widest text-blue-700 mb-3">
              Examples
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">
              Sites that look great on every device.
            </h2>
          </div>
          <p className="lg:col-span-5 text-gray-600 text-base leading-relaxed">
            Your site will be tailored to your trade — but here's the level of
            craft you can expect across desktop, tablet, and mobile.
          </p>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-xl bg-white"
          >
            <img
              src={IMAGES.hero_mockup}
              alt="Desktop website example"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs text-gray-700 border border-gray-200">
              <Monitor size={14} className="text-blue-700" /> Desktop
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:block absolute -bottom-10 left-8 w-56 rounded-xl overflow-hidden border border-gray-200 shadow-xl bg-white"
          >
            <img
              src={IMAGES.industry_restaurant}
              alt="Tablet website example"
              className="w-full h-40 object-cover"
              loading="lazy"
            />
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-white/90 backdrop-blur px-2 py-1 rounded-full text-[10px] text-gray-700 border border-gray-200">
              <Tablet size={12} className="text-blue-700" /> Tablet
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden md:block absolute -bottom-16 right-8 w-40 rounded-xl overflow-hidden border border-gray-200 shadow-2xl bg-white"
          >
            <img
              src={IMAGES.portfolio_mobile}
              alt="Mobile website example"
              className="w-full h-56 object-cover"
              loading="lazy"
            />
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-white/90 backdrop-blur px-2 py-1 rounded-full text-[10px] text-gray-700 border border-gray-200">
              <Smartphone size={12} className="text-blue-700" /> Mobile
            </div>
          </motion.div>
        </div>

        <div className="mt-24 grid sm:grid-cols-3 gap-4 text-center">
          {[
            { num: "60+", label: "Local businesses online" },
            { num: "7–14", label: "Days to launch" },
            { num: "4.9★", label: "Owner satisfaction" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-xl border border-gray-200 bg-white py-8"
              data-testid={`portfolio-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="font-display font-extrabold text-4xl text-gray-900">
                {s.num}
              </div>
              <div className="text-sm text-gray-600 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
